import { asString } from '../parser';
import type { Rule } from './types';

const KEBAB = /^[a-z][a-z0-9]*(-[a-z0-9]+)*$/;

export const nameRules: Rule[] = [
  {
    id: 'name.present',
    category: 'name',
    severity: 'fail',
    weight: 4,
    title: '`name` field present',
    appliesTo: 'both',
    run(ctx) {
      const name = asString(ctx.parsed.frontmatter.name).trim();
      if (!name) {
        return {
          status: 'fail',
          message: 'Missing `name` field in frontmatter.',
          fix: 'Add `name: your-skill-name` (kebab-case).',
        };
      }
      return { status: 'pass', message: `\`${name}\`` };
    },
  },
  {
    id: 'name.kebab-case',
    category: 'name',
    severity: 'fail',
    weight: 4,
    title: 'Name is kebab-case',
    appliesTo: 'both',
    run(ctx) {
      const name = asString(ctx.parsed.frontmatter.name).trim();
      if (!name) return { status: 'skipped', message: 'No name to check.' };
      if (KEBAB.test(name)) return { status: 'pass', message: 'Valid kebab-case.' };

      const issues: string[] = [];
      if (/\s/.test(name)) issues.push('contains spaces');
      if (/[A-Z]/.test(name)) issues.push('has capital letters');
      if (/_/.test(name)) issues.push('uses underscores');
      if (/^[0-9]/.test(name)) issues.push('starts with a digit');
      if (issues.length === 0) issues.push('uses disallowed characters');

      return {
        status: 'fail',
        message: `Name \`${name}\` ${issues.join(', ')}.`,
        fix: 'Use only lowercase letters, digits, and hyphens. Example: `notion-project-setup`.',
      };
    },
  },
  {
    id: 'name.not-reserved',
    category: 'name',
    severity: 'warn',
    weight: 2,
    title: 'Name is not reserved',
    appliesTo: 'both',
    run(ctx) {
      const name = asString(ctx.parsed.frontmatter.name).trim().toLowerCase();
      if (!name) return { status: 'skipped', message: 'No name to check.' };
      if (name === 'claude' || name === 'anthropic') {
        return {
          status: 'fail',
          message: 'Name `claude` and `anthropic` are reserved.',
          fix: 'Pick a name that describes your skill\'s domain.',
        };
      }
      if (name.startsWith('claude-') || name.startsWith('anthropic-')) {
        return {
          status: 'warn',
          message: `Names starting with \`${name.split('-')[0]}-\` are reserved.`,
          fix: 'Drop the prefix unless this skill is published by the upstream platform.',
        };
      }
      return { status: 'pass', message: 'Not reserved.' };
    },
  },
  {
    id: 'name.length',
    category: 'name',
    severity: 'warn',
    weight: 1,
    title: 'Name length is reasonable',
    appliesTo: 'both',
    run(ctx) {
      const name = asString(ctx.parsed.frontmatter.name).trim();
      if (!name) return { status: 'skipped', message: 'No name to check.' };
      if (name.length < 3) return { status: 'warn', message: 'Name is very short.' };
      if (name.length > 64) return { status: 'warn', message: 'Name is unusually long (>64 chars).' };
      return { status: 'pass', message: `${name.length} chars.` };
    },
  },
  {
    id: 'name.matches-folder',
    category: 'name',
    severity: 'info',
    weight: 1,
    title: 'Name matches folder name',
    appliesTo: 'github',
    run(ctx) {
      const name = asString(ctx.parsed.frontmatter.name).trim();
      if (!name || !ctx.folderName) return { status: 'skipped', message: 'Skipped.' };
      const normalized = name.toLowerCase().replace(/\s+/g, '-');
      const folderNormalized = ctx.folderName.toLowerCase();
      if (name === ctx.folderName) return { status: 'pass', message: 'Exact match.' };
      if (normalized === folderNormalized) return { status: 'pass', message: 'Match (case/spacing normalized).' };
      // Many skills legitimately scope their name with a prefix (e.g. `unity-workflow` in `workflow/`).
      if (normalized.endsWith(`-${folderNormalized}`) || folderNormalized.endsWith(`-${normalized}`)) {
        return { status: 'pass', message: 'Name shares the folder name as a suffix/prefix.' };
      }
      return {
        status: 'warn',
        message: `Frontmatter \`name\` (\`${name}\`) does not match folder name (\`${ctx.folderName}\`).`,
        fix: 'Align the folder name with the `name` field. Keeps installs and references consistent.',
      };
    },
  },
];
